import { useRef } from 'react'
import { SubmitHandler } from 'react-hook-form'
import { CAPIHttpClient } from '@grammarly/http-clients-capi'
import { Document } from '@contentful/rich-text-types'

import { feMetrics } from '@lib/tracking/metrics'
import { useApiService } from '@lib/hooks'
import { delay } from '@lib/utils/promise'
import { useGgoActions } from '@lib/services/api/cheetah-provider'
import { assertNever } from '@lib/utils/assert-never'
import { rhfToFormData } from '@components/form/rhf-helpers'
import * as CmsMetrics from '../../lib/tracking/metrics/metrics'
import { logError } from '../../lib/tracking/felog'
import { transformToObject, transformToString } from '../../lib/utils/error'
import { gnarSpec } from '@lib/tracking/gnar'

import {
  ToolApiError,
  toolErrorToString,
  toolTypeToLogErrorMetric,
  ToolType,
  toolTypeToMetricToolType,
  ToolApi,
  WallGuardConfig,
  WallGuardResult,
  ToolOnSubmitParams,
  isGoTemplatesToolType,
} from './types'
import { minimumLoadingTime } from './constants'
import { AIDetectorOutput } from './output/ai-detector'
import { PlagiarismCheckerOutput } from './output/plagiarism-checker'
import { wallGuardTracking } from './tracking'
import {
  analyzeTextAndMaybeTruncateToDesiredWordLimit,
  isSampleTextSubmission,
  parseSingleFieldFormData,
} from './utils'

export const useToolApi = (
  toolType: ToolType,
  toolInputName: string,
  setOutputContent: (outputContent: React.ReactNode) => void,
  setNextBestActionCopy: (nextBestActionCopy: string | null) => void,
  llmPrompt?: Document
) => {
  const { capi } = useApiService()
  const { sendPrompt } = useGgoActions()
  const toolApi = useRef<ToolApi>(genericRequest())

  switch (toolType) {
    case 'aiDetector':
      toolApi.current = AIDetectorRequest(toolInputName, capi, setOutputContent, setNextBestActionCopy)
      break
    case 'grammarChecker':
      toolApi.current = genericRequest()
      break
    case 'plagiarismChecker':
      toolApi.current = PlagiarismCheckerRequest(toolInputName, capi, setOutputContent, setNextBestActionCopy)
      break
    case 'paraphraser':
    case 'humanizer':
    case 'goTemplatesGeneric':
      toolApi.current = llmPrompt ? GoTemplateRequest(llmPrompt, sendPrompt) : genericRequest()
      break
    default:
      assertNever(toolType)
  }

  return toolApi.current
}

const AIDetectorRequest = (
  toolInputName: string,
  capi: CAPIHttpClient,
  setOutputContent: (outputContent: React.ReactNode) => void,
  setNextBestActionCopy: (nextBestActionCopy: string | null) => void
) => {
  return async (formData: FormData) => {
    try {
      const userText = parseSingleFieldFormData(toolInputName, formData)
      const response = await capi.detectAI(userText)

      setOutputContent(<AIDetectorOutput aiPercent={response.score} />)
      if (response.score === 0) {
        setNextBestActionCopy('Great! Improve quality with grammar, tone, and citation checks.')
      } else {
        setNextBestActionCopy('Pinpoint where AI might’ve been used and get natural-sounding fixes.')
      }
    } catch (err) {
      throw new ToolApiError(`AI Detector Request Failed: ${transformToString(err as object)}`)
    }
  }
}

const PlagiarismCheckerRequest = (
  toolInputName: string,
  capi: CAPIHttpClient,
  setOutputContent: (outputContent: React.ReactNode) => void,
  setNextBestActionCopy: (nextBestActionCopy: string | null) => void
) => {
  return async (formData: FormData) => {
    try {
      const userText = parseSingleFieldFormData(toolInputName, formData)
      const response = await capi.checkText(userText)

      setOutputContent(<PlagiarismCheckerOutput response={response} />)
      setNextBestActionCopy('View matches, add citations fast, and get help with smart rewrite suggestions.')
    } catch (err) {
      throw new ToolApiError(`Plagiarism Checker Request Failed: ${transformToString(err as object)}`)
    }
  }
}

const GoTemplateRequest = (
  llmPrompt: Document,
  sendPrompt: (llmPrompt: Document, formData: FormData) => Promise<boolean>
) => {
  return async (formData: FormData) => {
    try {
      const success = await sendPrompt(llmPrompt, formData)
      if (!success) {
        throw new ToolApiError('Sending prompt failed')
      }
    } catch (err) {
      if (err instanceof ToolApiError) {
        throw err
      }

      throw new ToolApiError(`Go Templates Single Step Request Failed: ${transformToString(err as object)}`)
    }
  }
}

const genericRequest = () => {
  return async (formData: FormData) => {
    console.error('Sending a generic request for the tool. Please fix this.', formData)
    throw new ToolApiError('Request failed and will not work. Please contact support.')
  }
}

// @todo Temporary functions to track success and failure for tools. Plan to standardize this (https://grammarly.atlassian.net/browse/G2A-1967).
const toolSuccessGnarTracking = (toolType: ToolType, pageId: string, slug: string) => {
  if (toolType === 'aiDetector') {
    gnarSpec.aiDetectorGenerateSuccess(pageId, slug)
  } else if (toolType === 'plagiarismChecker') {
    gnarSpec.checkTextFormFormSuccess(pageId, slug)
  }
}

const toolFailureGnarTracking = (toolType: ToolType, pageId: string, slug: string, error: unknown) => {
  const errorString = transformToString(error as object)
  if (toolType === 'aiDetector') {
    gnarSpec.aiDetectorGenerateFailure(pageId, slug, errorString)
  } else if (toolType === 'plagiarismChecker') {
    gnarSpec.checkTextFormFormFail(pageId, slug, errorString)
  }
}

export const createWallGuard = (config: WallGuardConfig) => {
  return (): WallGuardResult => {
    if (config.shouldTriggerGoTemplatesWall) {
      return { shouldWall: true, wallToTrigger: 'goTemplates', wallTrigger: config.triggerUsageWall }
    }

    if (config.toolUsageTracker && !config.toolUsageTracker.checkToolUsageState().canUse) {
      return { shouldWall: true, wallToTrigger: 'usage', wallTrigger: config.triggerUsageWall }
    }

    if (config.shouldTriggerTextLimitWall) {
      return { shouldWall: true, wallToTrigger: 'textLimit', wallTrigger: config.triggerTextLimitWall }
    }

    return { shouldWall: false }
  }
}

export const toolOnSubmit = ({
  toolType,
  toolApi,
  wallGuard,
  setIsLoading,
  setToolError,
  pageId,
  slug,
  toolUsageTracker,
  sampleText,
  toolInputName,
  setShowToolsGoogleOneTap,
  isGoogleOneTapOnTaskComplete,
  isToolsRedesign2_3AnonEnabled,
  setTextLimitExceededCopy,
  maxWordLimit,
}: ToolOnSubmitParams): SubmitHandler<Record<string, string>> => {
  return async (data, e) => {
    e?.preventDefault()

    setToolError(null)

    feMetrics
      .getMetric(CmsMetrics.GeneralClientMetrics.ToolUser)
      .getCounter(CmsMetrics.ClientMetricsActions.Submit)
      .setContext({
        toolPage: toolTypeToMetricToolType[toolType],
      })
      .increment()

    const newData = { ...data }

    const wallGuardResult = wallGuard()
    if (wallGuardResult.shouldWall) {
      if (isToolsRedesign2_3AnonEnabled && wallGuardResult.wallToTrigger === 'textLimit') {
        setTextLimitExceededCopy(
          `Only the first ${maxWordLimit} words were used, check all your text and get help with rewrite suggestions.`
        )

        const truncatedText = analyzeTextAndMaybeTruncateToDesiredWordLimit(
          newData[toolInputName],
          maxWordLimit
        ).truncatedText

        if (truncatedText) {
          newData[toolInputName] = truncatedText
        }
      } else {
        wallGuardResult.wallTrigger()
        wallGuardTracking(wallGuardResult, pageId, slug)

        return
      }
    } else {
      setTextLimitExceededCopy(undefined)
    }

    const formData = rhfToFormData(newData)

    if (isGoTemplatesToolType(toolType)) {
      try {
        await toolApi(formData)
      } catch (err) {
        setToolError(toolErrorToString(err))
        console.error(`Error sending prompt:`, err)
        logError(`Error sending prompt`, toolTypeToLogErrorMetric[toolType], {
          error: transformToObject(err as Error),
        })
      }
    } else {
      setIsLoading(true)

      try {
        feMetrics
          .getMetric(CmsMetrics.GeneralClientMetrics.ToolApi)
          .getCounter(CmsMetrics.ClientMetricsActions.Attempt)
          .setContext({
            toolPage: toolTypeToMetricToolType[toolType],
          })
          .increment()

        await Promise.all([toolApi(formData), delay(minimumLoadingTime)])

        feMetrics
          .getMetric(CmsMetrics.GeneralClientMetrics.ToolApi)
          .getCounter(CmsMetrics.ClientMetricsActions.Success)
          .setContext({
            toolPage: toolTypeToMetricToolType[toolType],
          })
          .increment()

        // Only increment usage if not submitting sample text
        const submittedText = parseSingleFieldFormData(toolInputName, formData)
        const isUsingSampleText = isSampleTextSubmission(submittedText, sampleText)

        if (toolUsageTracker && !isUsingSampleText) {
          toolUsageTracker.incrementToolUsageState()
        }
        toolSuccessGnarTracking(toolType, pageId, slug)
        if (isGoogleOneTapOnTaskComplete()) {
          setShowToolsGoogleOneTap(true)
        }
      } catch (err) {
        setToolError(toolErrorToString(err))
        console.error(`Error for tool type [${toolType}]:`, err)
        logError(`Error generating [${toolType}] response`, toolTypeToLogErrorMetric[toolType], {
          error: transformToObject(err as Error),
        })

        feMetrics
          .getMetric(CmsMetrics.GeneralClientMetrics.ToolApi)
          .getCounter(CmsMetrics.ClientMetricsActions.Failure)
          .setContext({
            toolPage: CmsMetrics.ToolType.AiDetector,
          })
          .increment()

        toolFailureGnarTracking(toolType, pageId, slug, err)
      } finally {
        setIsLoading(false)
      }
    }
  }
}
